import * as promotionsModule from "./promotions"

export interface Product {
  id: string
  name: string
  description: string
  priceInCents: number
  features: string[]
}

export type PlanName = "free" | "creator" | "dreamer"

export interface PlanDetails {
  name: string
  description: string
  price: string
  priceInCents: number
  features: string[]
  promotion?: {
    originalPrice: string
    discountedPrice: string
    discountPercentage: number
    badgeText?: string
    showCountdown?: boolean
    endDate?: string
  }
}

// Subscription plans for Planami
export const PRODUCTS: Product[] = [
  {
    id: "free-trial",
    name: "Free trial",
    description: "Perfect for starters: 7 days to like it",
    priceInCents: 0, // $0.00
    features: [
      "Basic daily & weekly views",
      "5 custom stickers",
      "3 planner books",
      "Basic templates",
    ],
  },
  {
    id: "creator-monthly",
    name: "Creator Plan",
    description: "Perfect for dedicated planners",
    priceInCents: 500, // $5.00
    features: [
      "All views (year, month, week, daily)",
      "Unlimited stickers & decorations",
      "3 planners: Calendar, Books & Movies, and Wishes",
      "Unlimited date range (access past and future)",
      "Basic templates: minimal, colorful",
      "Endless of history",
      // "Goal setting module",
    ],
  },
  {
    id: "creator-yearly",
    name: "Creator Plan",
    description: "Perfect for dedicated planners",
    priceInCents: 5000, // $50.00/year (equivalent to $4.17/month)
    features: [
      "All views (year, month, week, daily)",
      "Unlimited stickers & decorations",
      "3 planners: Calendar, Books & Movies, and Wishes",
      "Unlimited date range (access past and future)",
      "Basic templates: minimal, colorful",
      "Endless history",
    ],
  },
  {
    id: "dreamer-monthly",
    name: "Dreamer Plan",
    description: "Unlimited everything for serious dreamers",
    priceInCents: 900, // $9.00
    features: [
      "All views (year, month, week, daily)",
      "Unlimited stickers & decorations",
      "All planner modules: Calendar, Books & Movies, Wishes, Big Goals, Moodboard, Travel, Date Nights, Financial", //Gantt, Meditation
      "Unlimited date range (access past and future)",
      "Premium templates: retrospective, wellness",
      "Endless history",
      "Priority support",
    ],
  },
  {
    id: "dreamer-yearly",
    name: "Dreamer Plan",
    description: "Unlimited everything for serious dreamers",
    priceInCents: 9000, // $90.00/year (equivalent to $7.50/month)
    features: [
      "All views (year, month, week, daily)",
      "Unlimited stickers & decorations",
      "All planner modules: Calendar, Books & Movies, Wishes, Big Goals, Moodboard, Travel, Date Nights, Financial",
      "Unlimited date range (access past and future)",
      "Premium templates: retrospective, wellness",
      "Endless history",
      "Priority support",
    ],
  },
]

/**
 * Get product by plan name and interval (e.g., "creator", "monthly" -> "creator-monthly")
 */
export function getProductByPlanName(planName: PlanName, interval: "monthly" | "yearly" = "monthly"): Product | undefined {
  if (planName === "free") {
    return PRODUCTS.find((p) => p.id === "free-trial")
  }
  return PRODUCTS.find((p) => p.id === `${planName}-${interval}`)
}

/**
 * Promotions are imported statically on purpose.
 *
 * This used to be a dynamic import, which left getPlanDetails() promotion-blind
 * until the module promise resolved. In the static export that meant prices and
 * savings were prerendered without the promo and re-rendered with it on the
 * client — a hydration mismatch plus a visible "Save 17%" → "Save 67%" flash on
 * the home page. lib/promotions.ts imports nothing, so there is no cycle to
 * avoid, and vi.mock('@/lib/promotions') intercepts a static import just the
 * same.
 */
function getPromotionsModule(): any {
  return promotionsModule
}

/**
 * Get plan details formatted for UI display
 * Includes promotional pricing if available
 */
export function getPlanDetails(planName: PlanName, interval: "monthly" | "yearly" = "monthly"): PlanDetails | null {
  const product = getProductByPlanName(planName, interval)
  if (!product) return null

  const nameMap: Record<PlanName, string> = {
    free: "Free trial",
    creator: "Creator",
    dreamer: "Dreamer",
  }

  // Check for active promotions
  let promotion: PlanDetails["promotion"] | undefined
  if (planName !== "free") {
    try {
      const promotions = getPromotionsModule()
      if (promotions) {
        const { getBestPromotion, formatDiscountedPrice } = promotions
        const activePromo = getBestPromotion(planName, interval)
        
        if (activePromo) {
          const priceInfo = formatDiscountedPrice(product.priceInCents, activePromo.discountPercentage, interval)
          promotion = {
            originalPrice: priceInfo.original,
            discountedPrice: priceInfo.discounted,
            discountPercentage: activePromo.discountPercentage,
            badgeText: activePromo.badgeText,
            showCountdown: activePromo.showCountdown,
            endDate: activePromo.endDate,
          }
        }
      }
    } catch (error) {
      // If promotions module doesn't exist or fails to load, continue without promotions
      // This allows tests to run without the promotions module
    }
  }

  // Format price for yearly plans
  let price: string
  if (product.priceInCents === 0) {
    price = "$0"
  } else if (interval === "yearly") {
    const yearlyPrice = product.priceInCents / 100
    price = `$${yearlyPrice}/year`
  } else {
    price = `$${product.priceInCents / 100}`
  }

  // Use discounted price if promotion exists
  if (promotion) {
    price = promotion.discountedPrice
  }

  return {
    name: nameMap[planName],
    description: product.description,
    price,
    priceInCents: promotion 
      ? Math.round(product.priceInCents * (1 - promotion.discountPercentage / 100))
      : product.priceInCents,
    features: product.features,
    promotion,
  }
}

/**
 * Calculate savings percentage for yearly plans
 */
export function getYearlySavings(planName: PlanName): number {
  if (planName === "free") return 0
  
  const monthlyProduct = getProductByPlanName(planName, "monthly")
  const yearlyProduct = getProductByPlanName(planName, "yearly")
  
  if (!monthlyProduct || !yearlyProduct) return 0
  
  const monthlyTotal = monthlyProduct.priceInCents * 12
  const yearlyTotal = yearlyProduct.priceInCents
  const savings = monthlyTotal - yearlyTotal
  const savingsPercent = Math.round((savings / monthlyTotal) * 100)
  
  return savingsPercent
}

/**
 * Calculate the yearly savings label shown in pricing UI.
 * This combines the standing yearly-plan savings with any active yearly promo.
 */
export function getYearlySavingsWithPromotion(planName: PlanName): number {
  if (planName === "free") return 0

  const baseSavings = getYearlySavings(planName)
  const yearlyPlan = getPlanDetails(planName, "yearly")
  const promoSavings = yearlyPlan?.promotion?.discountPercentage ?? 0

  return Math.min(baseSavings + promoSavings, 100)
}
